import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlContainer } from '@angular/forms';
import { TabComponent, TabsComponent } from '@components';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  mergeMap,
  switchMap,
} from 'rxjs/operators';
import { BaseFormGroupComponent } from '../../../shared/components/base-form-group/base-form-group.component';
import {
  controlValue$,
  controlValueChanges$,
  disableFormControls,
  enableFormControls,
} from '../../../shared/utils/form-control/form-control.utils';
import { clearFormControls } from '../../../shared/utils/form/form.utils';
import { AddressSearchResult } from '../../models/address-search-result/address-search-result.interface';
import { AddressTabIndex } from '../../models/address-tab/address-tab-index.enum';
import { FormAddress } from '../../models/form-address/form-address.interface';
import { AddressService } from '../../services/address/address.service';
import { formatAddressSearchResultToFormAddress } from '../../utils/address.utils';

@Component({
  selector: 'app-form-address-tabs',
  templateUrl: './address-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabComponent, TabsComponent],
  standalone: true,
})
export class AddressTabsComponent
  extends BaseFormGroupComponent<FormAddress>
  implements OnInit
{
  @Output() activeTabIndexChanged = new EventEmitter<AddressTabIndex>();

  activeTabIndex$ = new BehaviorSubject<AddressTabIndex>(
    AddressTabIndex.Search,
  );
  addressTabIndex: typeof AddressTabIndex = AddressTabIndex;

  private idControl?: AbstractControl | null;

  private readonly addressService = inject(AddressService);
  override readonly controlContainer = inject(ControlContainer);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.idControl = this.form.get('id');
    this.setInitialActiveTabIndex();
    this.loadAddressIdControlExistingValue();
    this.listenToAddressIdControlValue();
    this.listenToActiveTabIndex();
  }

  onTabSelection(index: AddressTabIndex): void {
    this.activeTabIndex$.next(index);
    this.activeTabIndexChanged.emit(index);
  }

  protected clear(): void {
    clearFormControls(
      this.form,
      'id',
      'type',
      'addressLine1',
      'addressLine2',
      'addressLine3',
      'addressLine4',
      'postalCode',
    );
  }

  private setInitialActiveTabIndex(): void {
    const value: FormAddress = this.getRawValue();

    if (value.postalCode && !value.id) {
      this.activeTabIndex$.next(AddressTabIndex.Manual);
    } else {
      this.activeTabIndex$.next(AddressTabIndex.Search);
    }
  }

  private loadAddressIdControlExistingValue = (): void => {
    controlValue$<string>(this.idControl)
      .pipe(
        first(),
        filter(Boolean),
        mergeMap((id?: string) => this.getAddressById(id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((address?: FormAddress) => {
        if (address) {
          this.patchValue(address);
        }
      });
  };

  private listenToAddressIdControlValue = (): void => {
    controlValueChanges$<string>(this.idControl)
      .pipe(
        switchMap((id: string | undefined) => {
          if (!id) {
            return of(undefined);
          }

          return this.getAddressById(id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((address?: FormAddress) => {
        if (address) {
          this.patchValue(address);
        }
      });
  };

  private listenToActiveTabIndex(): void {
    this.activeTabIndex$
      .pipe(
        map(
          (activeTabIndex: AddressTabIndex) =>
            activeTabIndex === AddressTabIndex.Search,
        ),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchEnabled: boolean) => {
        if (searchEnabled) {
          enableFormControls(this.idControl);
        } else {
          disableFormControls(this.idControl);
        }
      });
  }

  private getAddressById(id?: string): Observable<FormAddress | undefined> {
    if (!id) {
      return EMPTY;
    }

    disableFormControls(this.idControl);
    return this.addressService.getAddressById(id).pipe(
      map((address: AddressSearchResult) =>
        formatAddressSearchResultToFormAddress(address),
      ),
      finalize(() => enableFormControls(this.idControl)),
      catchError(() => {
        return of(undefined);
      }),
    );
  }
}
